import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';

/**
 * AppStoreRating - official, live App Store rating badge.
 *
 * Pulls the real rating + count from /api/appstore/rating (backed by Apple's
 * iTunes lookup, cached daily). Renders nothing if the rating is unavailable,
 * so it never shows a fabricated/stale number.
 *
 * variant: 'default' (app-promo) | 'compact' (footer) | 'inline' (hero line)
 */
export const AppStoreRating = ({ variant = 'default', dark = false, className = '' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appstore/rating`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (alive && d && d.rating) setData(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!data) return null;

  const { rating, ratingCount, url } = data;
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));

  const Stars = ({ size }) => (
    <span className="relative inline-flex" aria-hidden="true">
      <span className="flex" style={{ color: '#d2d2d7' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={`b${i}`} className={size} strokeWidth={0} fill="currentColor" />
        ))}
      </span>
      <span className="flex absolute inset-0 overflow-hidden" style={{ width: `${pct}%`, color: '#f5a623' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={`f${i}`} className={`${size} shrink-0`} strokeWidth={0} fill="currentColor" />
        ))}
      </span>
    </span>
  );

  if (variant === 'compact') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="appstore-rating-compact"
        className={`inline-flex items-center gap-1.5 text-[13px] transition-colors ${
          dark ? 'text-white/70 hover:text-white' : 'text-[#6e6e73] hover:text-[#1d1d1f]'
        } ${className}`}
      >
        <Stars size="w-3.5 h-3.5" />
        <span className={`tabular-nums font-semibold ${dark ? 'text-white' : 'text-[#1d1d1f]'}`}>{rating.toFixed(1)}</span>
        <span>on the App Store</span>
      </a>
    );
  }

  if (variant === 'inline') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="appstore-rating-inline"
        className={`inline-flex items-center gap-1.5 ${className}`}
      >
        <Stars size="w-3.5 h-3.5" />
        <span className="tabular-nums font-medium">{rating.toFixed(1)}</span>
        <span className="opacity-90">on the App Store</span>
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="appstore-rating"
      className={`inline-flex items-center gap-3 rounded-2xl border border-[#e8e8ed] bg-white px-4 py-3 hover:border-[#d2d2d7] transition-colors ${className}`}
    >
      <Stars size="w-5 h-5" />
      <span className="flex flex-col leading-tight">
        <span className="text-[15px] font-bold text-[#1d1d1f] tabular-nums">
          {rating.toFixed(1)} <span className="text-[#6e6e73] font-medium">on the App Store</span>
        </span>
        <span className="text-[12px] text-[#86868b] tabular-nums">
          {ratingCount.toLocaleString()} {ratingCount === 1 ? 'rating' : 'ratings'} · verified by Apple
        </span>
      </span>
    </a>
  );
};

export default AppStoreRating;
